/*
nativeScroll.tsx
Copyright © 2025 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react'
import {
  scrollerClickEvent,
  ScrollWrapperProps,
  ScrollWrapperRef
} from './scrollWrapper'


const NativeScroll = forwardRef<
  ScrollWrapperRef,
  ScrollWrapperProps
>((props, ref) => {
  const {
    horizontal,
    children,
    className,
    style,
    contentClassName,
    contentStyle,
    scrollId,
    onScroll
  } = props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const indicatorThumbRef = useRef<HTMLDivElement | null>(null)

  const updateIndicator = useCallback(() => {
    if (horizontal || !containerRef.current || !indicatorThumbRef.current) {
      return
    }
    const viewportHeight = containerRef.current.clientHeight
    const contentHeight = containerRef.current.scrollHeight
    if (viewportHeight <= 0) {
      return
    }
    const maxScrollTop = Math.max(contentHeight - viewportHeight, 0)
    const thumbHeight = maxScrollTop === 0
      ? viewportHeight
      : Math.max(24, Math.round((viewportHeight * viewportHeight) / contentHeight))
    const maxThumbOffset = Math.max(viewportHeight - thumbHeight, 0)
    const thumbOffset = maxScrollTop === 0
      ? 0
      : Math.round((containerRef.current.scrollTop / maxScrollTop) * maxThumbOffset)

    indicatorThumbRef.current.style.height = `${thumbHeight}px`
    indicatorThumbRef.current.style.transform = `translateY(${thumbOffset}px)`
  }, [horizontal])

  useImperativeHandle(ref, () => ({
    scrollTo: (position: number) => {
      if (!containerRef.current) {
        return
      }
      const nativePosition = Math.max(0, Math.abs(position))
      if (horizontal) {
        containerRef.current.scrollLeft = nativePosition
      } else {
        containerRef.current.scrollTop = nativePosition
      }
      updateIndicator()
    },
    refresh: () => {}
  }), [horizontal, updateIndicator])

  const handleScroll = () => {
    updateIndicator()
    if (!containerRef.current || !onScroll) {
      return
    }
    const x = horizontal ? -containerRef.current.scrollLeft : 0
    const y = horizontal ? 0 : -containerRef.current.scrollTop
    onScroll({ x, y, scrollId })
  }

  useEffect(() => {
    updateIndicator()
  }, [children, contentStyle, updateIndicator])

  useEffect(() => {
    window.addEventListener('resize', updateIndicator)
    return () => {
      window.removeEventListener('resize', updateIndicator)
    }
  }, [updateIndicator])
  const emitClickPosition = (event: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
    let clientX = 0
    let clientY = 0
    if ('touches' in event && event.touches.length > 0) {
      clientX = event.touches[0].clientX
      clientY = event.touches[0].clientY
    } else if ('changedTouches' in event && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX
      clientY = event.changedTouches[0].clientY
    } else if ('clientX' in event) {
      clientX = event.clientX
      clientY = event.clientY
    }
    window.dispatchEvent(
      new CustomEvent(scrollerClickEvent, {
        bubbles: true,
        detail: { x: clientX, y: clientY, scrollId }
      })
    )
  }

  const containerClasses = [
    'scroll-wrapper__container',
    'scroll-wrapper__container--native',
    horizontal ? 'scroll-wrapper__container--horizontal' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ')

  const contentClasses = [
    'scroll-wrapper__content',
    horizontal ? 'scroll-wrapper__content--horizontal' : '',
    contentClassName ?? ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      style={{
        position: 'relative',
        height: '100%',
        overflowX: horizontal ? 'scroll' : 'hidden',
        overflowY: horizontal ? 'hidden' : 'scroll',
        ...style
      }}
      onScroll={handleScroll}
      onPointerUp={emitClickPosition}
      onMouseUp={emitClickPosition}
      onTouchEnd={emitClickPosition}
    >
      <div className={contentClasses} style={contentStyle}>
        {children}
      </div>
    </div>
  )
})

export default NativeScroll
